import { useQuery, UseQueryResult } from '@tanstack/react-query';
import { API } from 'api';
import { AQISensorObservation } from '../../aqiSensors.types';

type UseAQISensorObservationsProps = {
  id: string | number;
  start: Date;
  end: Date;
  pollInterval?: number;
};

type UseAQISensorObservations = Omit<
  UseQueryResult<AQISensorObservation[], Error>,
  'data'
> & {
  observations: AQISensorObservation[];
};

export type ServerAQISensorObservation = {
  timestamp: string;
  values: {
    aqi: number | null;
  };
};

type FetchObservationsValues = {
  id: string | number;
  start: Date;
  end: Date;
};

const fetchAQISensorObservations = async (
  values: FetchObservationsValues,
): Promise<AQISensorObservation[]> => {
  const { id, start, end } = values;
  const response = await API.get<ServerAQISensorObservation[]>(
    `weather/observations/${id}/`,
    {
      params: {
        start: start.toISOString(),
        end: end.toISOString(),
      },
    },
  );

  return (
    response.data
      // Filter out observations with null AQI values
      .filter((observation) => observation.values.aqi !== null)
      .map((observation) => {
        return {
          timestamp: new Date(observation.timestamp),
          ...observation.values,
        };
      }) as AQISensorObservation[]
  );
};

export const useAQISensorObservations = (
  props: UseAQISensorObservationsProps,
): UseAQISensorObservations => {
  const { id, start, end, pollInterval } = props;

  const { data: observations = [], ...results } = useQuery({
    queryKey: ['AQISensorObservations', id],
    queryFn: () => fetchAQISensorObservations({ id, start, end }),
    refetchInterval: pollInterval,
  });

  return {
    ...results,
    observations,
  };
};
